import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { Button } from "@/components/ui/button";
import api from "@/utils/api";
import { toast } from "sonner";
import { Plus, Search, Loader2, Download, Copy, Settings2 } from "lucide-react";

const fmt = (n) => new Intl.NumberFormat("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(Number(n) || 0);
const CHIP = {
  DRAFT: "bg-slate-100 text-slate-700", SENT: "bg-blue-100 text-blue-700", ACCEPTED: "bg-emerald-100 text-emerald-700",
  REJECTED: "bg-red-100 text-red-700", EXPIRED: "bg-amber-100 text-amber-700",
};
const STATUSES = ["ALL", "DRAFT", "SENT", "ACCEPTED", "REJECTED", "EXPIRED"];
const sel = "h-9 rounded-md border border-slate-300 bg-white px-2.5 text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-[#159DFF]";

export default function Quotations() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [status, setStatus] = useState("ALL");
  const [q, setQ] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [loading, setLoading] = useState(true);
  const pageSize = 25;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/v2/quotations", { params: { status, q: q || undefined, from_date: from || undefined, to_date: to || undefined, page, page_size: pageSize } });
      setRows(r.data.items); setTotal(r.data.total);
    } catch { toast.error("Could not load quotations"); }
    finally { setLoading(false); }
  }, [status, q, from, to, page]);

  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);
  useEffect(() => { setPage(1); }, [status, q, from, to]);

  const pdf = async (r) => {
    try {
      const res = await api.get(`/v2/quotations/${r.id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = `Sendtoo_Quote_${r.quote_number.replace(/\//g, "-")}.pdf`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch { toast.error("Could not download PDF"); }
  };
  const clone = async (r) => {
    try { const res = await api.post(`/v2/quotations/${r.id}/clone`); toast.success(`Cloned as ${res.data.quote_number}`); navigate(`/admin/quotations/${res.data.id}`); }
    catch (e) { toast.error(e.response?.data?.detail || "Clone failed"); }
  };

  const pages = Math.max(1, Math.ceil(total / pageSize));

  return (
    <DashboardLayout title="Quotations">
      <div className="p-4 sm:p-6 max-w-7xl mx-auto" data-testid="quotations-page">
        <div className="flex flex-wrap items-center gap-3 mb-5">
          <h1 className="text-xl sm:text-2xl font-bold text-slate-900">Quotations</h1>
          <div className="ml-auto flex gap-2">
            <Button variant="outline" size="sm" onClick={() => navigate("/admin/quotations/catalog")}><Settings2 className="w-4 h-4 mr-1.5" />Service catalog</Button>
            <Button size="sm" className="bg-[#159DFF] hover:bg-[#0F84D9] text-white" onClick={() => navigate("/admin/quotations/new")} data-testid="new-quotation"><Plus className="w-4 h-4 mr-1.5" />New quotation</Button>
          </div>
        </div>

        <div className="flex flex-wrap gap-2 mb-4">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="w-4 h-4 absolute left-2.5 top-2.5 text-slate-400" />
            <input className={sel + " w-full pl-8"} placeholder="Search quote no, customer or GSTIN" value={q} onChange={(e) => setQ(e.target.value)} />
          </div>
          <select className={sel} value={status} onChange={(e) => setStatus(e.target.value)}>{STATUSES.map((s) => <option key={s}>{s}</option>)}</select>
          <input type="date" className={sel} value={from} onChange={(e) => setFrom(e.target.value)} aria-label="From date" />
          <input type="date" className={sel} value={to} onChange={(e) => setTo(e.target.value)} aria-label="To date" />
        </div>

        <div className="bg-white border border-slate-200 rounded-xl overflow-x-auto">
          <table className="w-full text-sm min-w-[820px]">
            <thead className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-500">
              <tr>{["Quote no", "Date", "Customer", "Services", "Grand total", "Valid until", "Status", ""].map((h) => <th key={h} className="text-left font-semibold px-3 py-2.5">{h}</th>)}</tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={8} className="py-10 text-center"><Loader2 className="w-5 h-5 animate-spin inline text-slate-400" /></td></tr>}
              {!loading && rows.length === 0 && <tr><td colSpan={8} className="py-10 text-center text-slate-400">No quotations found. Create your first one.</td></tr>}
              {!loading && rows.map((r) => (
                <tr key={r.id} className="border-t border-slate-100 hover:bg-slate-50/70 cursor-pointer" onClick={() => navigate(`/admin/quotations/${r.id}`)}>
                  <td className="px-3 py-2.5 font-semibold text-slate-900">{r.quote_number}</td>
                  <td className="px-3 py-2.5 text-slate-600">{r.quote_date}</td>
                  <td className="px-3 py-2.5 text-slate-800">{r.customer_snapshot?.name}</td>
                  <td className="px-3 py-2.5 text-slate-500 max-w-[220px] truncate" title={r.services}>{r.services}</td>
                  <td className="px-3 py-2.5 font-semibold tabular-nums">₹{fmt(r.totals?.grand_total)}</td>
                  <td className="px-3 py-2.5 text-slate-600">{r.valid_until}</td>
                  <td className="px-3 py-2.5"><span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${CHIP[r.status]}`}>{r.status}</span></td>
                  <td className="px-3 py-2.5 whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                    <button className="p-1.5 text-slate-500 hover:text-[#159DFF]" title="Download PDF" onClick={() => pdf(r)}><Download className="w-4 h-4" /></button>
                    <button className="p-1.5 text-slate-500 hover:text-[#159DFF]" title="Clone" onClick={() => clone(r)}><Copy className="w-4 h-4" /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="flex items-center justify-between mt-3 text-sm text-slate-500">
          <span>{total} quotation{total === 1 ? "" : "s"}</span>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>Prev</Button>
            <span>Page {page} / {pages}</span>
            <Button variant="outline" size="sm" disabled={page >= pages} onClick={() => setPage((p) => p + 1)}>Next</Button>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
